import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import {
  FiCpu, FiKey, FiLock, FiLayout, FiCheck,
  FiSearch, FiPenTool, FiImage, FiHardDrive, FiShield
} from 'react-icons/fi';
import MicrosoftStoreBtn from '../components/MicrosoftStoreBtn';
import { getProduct } from '../data/catalog';
import Reel from '../components/Reel';

const FeatureCard = ({ icon, title, desc }) => (
  <div className="bg-card border border-border p-6 rounded-none hover:border-accent-emerald/50 transition-all group">
    <div className="w-12 h-12 rounded-md bg-accent-emerald/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
      <SafeIcon icon={icon} className="w-6 h-6 text-accent-emerald" />
    </div>
    <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
    <p className="text-muted text-sm leading-relaxed">{desc}</p>
  </div>
);

const Step = ({ number, title, desc }) => (
  <div className="flex gap-6 items-start">
    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent-emerald flex items-center justify-center text-white font-bold">
      {number}
    </div>
    <div>
      <h4 className="text-lg font-bold text-white mb-1">{title}</h4>
      <p className="text-muted text-sm">{desc}</p>
    </div>
  </div>
);

const PressVerse = () => {
  const PRODUCT = getProduct('pressverse');
  const STORE_LINK = PRODUCT.storeLink;

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-emerald/10 border border-accent-emerald/20 text-accent-emerald text-sm font-medium mb-6">
              <SafeIcon icon={FiCpu} /> Version 1.2.2 — out now
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight">
              From Idea to Amazon KDP<br />
              <span className="text-accent-emerald">In Minutes with PressVerse</span>
            </h1>
            <p className="text-xl text-muted max-w-3xl mx-auto mb-10 leading-relaxed">
              PressVerse is a Windows desktop app for authors and self-publishers. Research the niche,
              write the book, design the cover and generate the Amazon listing — with your own AI key,
              on your own machine.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <MicrosoftStoreBtn
                accentColor="bg-accent-emerald"
                className="px-10 py-4 scale-110"
                href={STORE_LINK}
              />
              <div className="flex items-center gap-2 text-sm text-muted">
                <SafeIcon icon={FiShield} className="text-accent-emerald" />
                <span>Lifetime Access: {PRODUCT.price}. No subscriptions.</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Features Grid */}
      <section className="py-20 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Total Publishing Control</h2>
            <p className="text-muted">Every stage of an Amazon KDP book, inside one app.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCard
              icon={FiSearch}
              title="Niche Research"
              desc="Check how well a category actually sells before you write. The BSR estimator turns an Amazon rank into sales and revenue figures."
            />
            <FeatureCard
              icon={FiPenTool}
              title="AI Writing"
              desc="Outlines, chapters and full manuscripts in six languages, using OpenAI, Claude, Gemini, DeepSeek or Mistral. A built-in Literary Critic reviews and rewrites what it produces."
            />
            <FeatureCard
              icon={FiImage}
              title="Cover Design"
              desc="Build a print-ready paperback cover — front, spine and back — sized to your trim and page count, with AI cover art."
            />
            <FeatureCard
              icon={FiLayout}
              title="Pro Formatting"
              desc="Export a KDP-ready PDF interior, a standards-compliant EPUB 3 and DOCX, with chapter version history kept along the way."
            />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">From Nothing to Listed</h2>
            <p className="text-muted">The same five steps, whether it is your first book or your fiftieth.</p>
          </div>
          <div className="space-y-10">
            <Step number="1" title="Find the niche" desc="Search a category, read the BSR estimates, and publish into a market that is actually buying." />
            <Step number="2" title="Write the book" desc="Set the genre, length and language. PressVerse builds the outline, then writes chapter by chapter — you can keep editing while it works." />
            <Step number="3" title="Sharpen it" desc="Run the Literary Critic over any chapter for a structured review, then take its rewrite or keep your own. Every version is saved." />
            <Step number="4" title="Design the cover" desc="Generate the art, set your trim size, and get a paperback cover laid out to KDP spine and bleed requirements." />
            <Step number="5" title="Publish" desc="Generate a keyword-optimised title, description and categories, then let the built-in browser agent fill in your KDP dashboard." />
          </div>
        </div>
      </section>

      {/* Reel — the frames for this product, so a visitor sees what it does. */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4">
          <p className="rule-label mb-6">Reel — PressVerse</p>
          <Reel frames={PRODUCT.frames} label="PressVerse" sound={false} ratio="aspect-[16/10]" />
        </div>
      </section>

      {/* BYOK / privacy */}
      <section className="py-20 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Your Key, Your Files, Your Book</h2>
            <p className="text-muted">We do not run a server for PressVerse. There is nothing of yours for us to hold.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FeatureCard
              icon={FiKey}
              title="Bring Your Own Key"
              desc="Plug in your own OpenAI, Anthropic, Gemini, DeepSeek, Mistral or Stability key. You pay the provider directly — no markup from us, no token limits set by us."
            />
            <FeatureCard
              icon={FiLock}
              title="Encrypted at Rest"
              desc="API keys and any saved Amazon KDP cookies are encrypted on your machine with the Windows Data Protection API, tied to your Windows account."
            />
            <FeatureCard
              icon={FiHardDrive}
              title="Everything Stays Local"
              desc="Projects, chapters and exports live in your own folders. No account, no telemetry, no analytics — and no cloud copy of your manuscript."
            />
          </div>
        </div>
      </section>

      {/* What's new */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="rule-label mb-6">New in 1.2.2</p>
          <div className="bg-card border border-border p-8 rounded-none">
            <ul className="space-y-4 text-sm text-muted">
              <li className="flex gap-3">
                <SafeIcon icon={FiCheck} className="text-accent-emerald flex-shrink-0 mt-1" />
                <span>API keys and Amazon KDP cookies are now encrypted at rest with Windows DPAPI.</span>
              </li>
              <li className="flex gap-3">
                <SafeIcon icon={FiCheck} className="text-accent-emerald flex-shrink-0 mt-1" />
                <span>EPUB export rebuilt on a new standards-compliant engine; PDF and EPUB now handle special characters correctly.</span>
              </li>
              <li className="flex gap-3">
                <SafeIcon icon={FiCheck} className="text-accent-emerald flex-shrink-0 mt-1" />
                <span>A Help &amp; Support centre inside the app: getting started, FAQ, privacy, terms and licences.</span>
              </li>
              <li className="flex gap-3">
                <SafeIcon icon={FiCheck} className="text-accent-emerald flex-shrink-0 mt-1" />
                <span>Data controls in Settings — open your data folder, clear the cache, reset preferences.</span>
              </li>
              <li className="flex gap-3">
                <SafeIcon icon={FiCheck} className="text-accent-emerald flex-shrink-0 mt-1" />
                <span>Cover generation now fails loudly instead of quietly producing a placeholder.</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-white mb-6">Stop Subscribing. Start Owning.</h2>
          <p className="text-muted mb-10">
            Why pay $65+ every month for limited AI tools? PressVerse is a professional Windows desktop app with a lifetime license. One payment, unlimited books.
          </p>
          <div className="bg-card border border-border p-10 rounded-none relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4">
              <span className="bg-accent-emerald text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest">One-Time Purchase</span>
            </div>
            <div className="text-6xl font-bold text-white mb-2">{PRODUCT.price}</div>
            <div className="text-muted text-sm mb-8 font-medium italic">One-time payment • Lifetime updates included</div>

            <div className="flex flex-col items-center gap-4">
              <MicrosoftStoreBtn
                accentColor="bg-accent-emerald"
                className="px-12 py-5 scale-110"
                href={STORE_LINK}
              />
            </div>

            <div className="flex flex-wrap items-center justify-center gap-6 text-xs text-muted mt-8">
              <span className="flex items-center gap-1"><SafeIcon icon={FiCheck} className="text-accent-emerald" /> Windows 10 (1809) or later, 64-bit</span>
              <span className="flex items-center gap-1"><SafeIcon icon={FiCheck} className="text-accent-emerald" /> Licensed through the Microsoft Store</span>
              <span className="flex items-center gap-1"><SafeIcon icon={FiCheck} className="text-accent-emerald" /> Writes in 6 languages</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PressVerse;
